/**
 * IMPORTS
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { Eye, Undo2 } from "lucide-react";

// components
import { Button } from "@components/button/button";
import { DynamicTable } from "@components/table-custom/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/tabs";

// components / pages
import { MinhaCobrancaCreditos } from "@pages/client/shipping-history/components/minha-cobranca-creditos";
import { SearchInputHistoricoRemessa } from "@pages/client/shipping-history/components/search-historico-remessa";

// hooks
import { useHistoricoRemessas } from "@/hooks/remessas/historico-de-remessa-do-cliente";

// store-zustand
import { useStoreZustandHistoricoRemessa } from "@/store-zustand/cliente/historico-de-remessa-do-cliente";
import { useStoreZustandRemessaEmAndamento } from "@/store-zustand/cliente/remessas-em-andamento";

// utils / features-flags
import { FEATURES, useFeature } from "@/utils/auth/features";

// helpers
import { dataTitleTableHistory } from "./helpers";

const ShippingHistoryPage = () => {
  const router = useNavigate();

  const { setRemessaEmAndamentoUnico } = useStoreZustandRemessaEmAndamento();
  const usuarioPodeVerCobrancaCredito = useFeature(FEATURES.VISUALIZAR_COBRANCA_CREDITO);

  const { historicoRemessas, historicoRemessasCarregando, paginations } = useHistoricoRemessas();
  const {
    currentPage,
    itemsPerPage,
    setCurrentPage,
    totalItemsPage,
    lastPage,
    clearPersistedData,
  } = useStoreZustandHistoricoRemessa();

  const onGoToPage = useCallback(
    (action: number | "prev" | "next") => {
      const newPage =
        action === "prev"
          ? Math.max(1, currentPage - 1)
          : action === "next"
            ? currentPage + 1
            : action;

      setCurrentPage(newPage);
    },
    [currentPage, setCurrentPage]
  );
  return (
    <>
      <Tabs defaultValue="all">
        <div className="flex items-center">
          <TabsList className="">
            <TabsTrigger value="all" className="">
              Históricos
            </TabsTrigger>
            {usuarioPodeVerCobrancaCredito && (
              <TabsTrigger value="minha-cobranca">Minha cobrança</TabsTrigger>
            )}
          </TabsList>
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant="default"
              className="h-8 gap-1 text-neutral-50"
              onClick={() => {
                clearPersistedData();
                router("/");
              }}
            >
              <Undo2 className="h-3.5 w-3.5" />
              <span className="sm:whitespace-nowra sr-only text-neutral-50 sm:not-sr-only">
                Voltar
              </span>
            </Button>
          </div>
        </div>
        <TabsContent value="all">
          <DynamicTable
            title="Histórico de remessas"
            description="Gerencie seu histórico de remessas e visualize quando quiser."
            isLoadingPage={historicoRemessasCarregando}
            data={historicoRemessas ?? []}
            totalItems={totalItemsPage}
            itemsPerPage={itemsPerPage}
            onGoToPage={onGoToPage}
            paginations={paginations}
            currentPage={currentPage}
            lastPage={lastPage}
            columns={dataTitleTableHistory}
            actions={[
              {
                icon: Eye,
                label: "Mais informações",
                textColor: "text-red-600",
                onClick: (item: any) => {
                  setRemessaEmAndamentoUnico(item);
                  router("/details-in-progress");
                },
              },
            ]}
            childerSearch={<SearchInputHistoricoRemessa />}
          />
        </TabsContent>
        {usuarioPodeVerCobrancaCredito && (
          <TabsContent value="minha-cobranca">
            <MinhaCobrancaCreditos />
          </TabsContent>
        )}
      </Tabs>
    </>
  );
};

/**
 * EXPORTS
 */
export { ShippingHistoryPage };
